import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {
    createMicrosites,
    getMicrositesBaseInfo,
    updateMicrosites,
} from 'src/openapi/admin/apis/MicroSitesBaseInfoApi';
import {useAppDispatch} from 'src/store/hooks';
import {fileToBase64} from 'src/utils/fileToBase64';

import {
    MICROSITE_DOMAINS_CONTROL,
    MICROSITE_DOMAINS_REQUIRED_ERROR,
    MICROSITE_DOMAINS_REQUIRED_MESSAGE,

    MICROSITE_LOGO_CONTROL,
    MICROSITE_LOGO_REQUIRED_ERROR,
    MICROSITE_LOGO_REQUIRED_MESSAGE,
    MICROSITE_LOGO_TO_BIG_ERROR,
    MICROSITE_LOGO_TO_BIG_MESSAGE,

    MICROSITE_NAME_CONTROL,
    MICROSITE_NAME_REQUIRED_ERROR,
    MICROSITE_NAME_REQUIRED_MESSAGE,
} from './constants';

import type {MicroSiteBaseInfo} from '../../typedef';
import type {Error} from 'src/openapi/admin/models/Error';
import type {SuccessPayload} from 'src/typedef';


type Props = {
    accountId: number,
    isNewMicroSite: boolean,
    onLoadNewMicroSite: () => void,
    onCreateMicroSite: () => void,
};

export const useMicroSiteBaseInfo = ({accountId, onCreateMicroSite, onLoadNewMicroSite, isNewMicroSite}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError, validationErrors} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const getResponse = await apiDispatch(getMicrositesBaseInfo({accountId}, {signal}));
        if (getResponse.payload.success) {
            const {data} = getResponse.payload;
            const payload: {success: true, error: false, data: MicroSiteBaseInfo} = {
                success: true,
                error: false,
                data: {
                    ...data,
                    micrositeName: data.micrositeName || '',
                    domains: data.domains.length ? data.domains : [''],
                    logoFile: null,
                },
            };

            if (!data.micrositeName) {
                onLoadNewMicroSite();
            }

            return Promise.resolve({type: '', payload});
        } else {
            const payload: {success: false, error: true, data: Error} = {
                success: false,
                error: true,
                data: getResponse.payload.data,
            };

            return Promise.resolve({type: '', payload});
        }
    }, [accountId, apiDispatch, onLoadNewMicroSite]);

    const saveDispatchAction = useCallback(async (microSiteBaseInfo: MicroSiteBaseInfo, signal: AbortSignal) => {
        if (isNewMicroSite && microSiteBaseInfo.logoFile) {
            const creationResult = await apiDispatch(createMicrosites({
                accountId,
                micrositeName: microSiteBaseInfo.micrositeName,
                logo: microSiteBaseInfo.logoFile,
                domains: microSiteBaseInfo.domains,
                accountPageInstructions: microSiteBaseInfo.accountPageInstructions,
                loginPageInstructions: microSiteBaseInfo.loginPageInstructions,
                loginPasswordPrompt: microSiteBaseInfo.loginPasswordPrompt,
                loginUserNamePrompt: microSiteBaseInfo.loginUserNamePrompt,
            }, {signal}));
            if (creationResult.payload.success) {
                const payload: SuccessPayload<undefined> = {
                    data: undefined,
                    success: true,
                    error: false,
                };
                onCreateMicroSite();
                return Promise.resolve({type: '', payload});
            } else {
                return creationResult;
            }
        } else {
            let microSiteLogo = microSiteBaseInfo.logo;
            if (microSiteBaseInfo.logoFile) {
                microSiteLogo = await fileToBase64(microSiteBaseInfo.logoFile);
            }

            return await apiDispatch(updateMicrosites({
                accountId,
                micrositeName: microSiteBaseInfo.micrositeName,
                body: microSiteLogo || '',
                domains: microSiteBaseInfo.domains,
                accountPageInstructions: microSiteBaseInfo.accountPageInstructions,
                loginPageInstructions: microSiteBaseInfo.loginPageInstructions,
                loginPasswordPrompt: microSiteBaseInfo.loginPasswordPrompt,
                loginUserNamePrompt: microSiteBaseInfo.loginUserNamePrompt,
            }, {signal}));
        }
    }, [accountId, isNewMicroSite, apiDispatch, onCreateMicroSite]);

    const editValidation = useCallback((microSiteBaseInfo: MicroSiteBaseInfo) => {
        const {micrositeName, domains, logo, logoFile} = microSiteBaseInfo;

        if (micrositeName) {
            removeError(MICROSITE_NAME_CONTROL);
        }

        if (logoFile || logo) {
            removeError(MICROSITE_LOGO_CONTROL, [MICROSITE_LOGO_REQUIRED_ERROR]);
        }

        if (microSiteBaseInfo.logoFile && microSiteBaseInfo.logoFile.size <= 10_000_000) {
            removeError(MICROSITE_LOGO_CONTROL, [MICROSITE_LOGO_TO_BIG_ERROR]);
        }

        domains.forEach((domain, i) => {
            if (domain) {
                removeError(`${MICROSITE_DOMAINS_CONTROL}-${i}`, [MICROSITE_DOMAINS_REQUIRED_ERROR]);
            }
        });
    }, [removeError]);

    const saveValidation = useCallback((microSiteBaseInfo: MicroSiteBaseInfo) => {
        let valid = true;

        if (validationErrors.length) {
            valid = false;
        }

        if (!microSiteBaseInfo.micrositeName) {
            addError({
                controlId: MICROSITE_NAME_CONTROL,
                code: MICROSITE_NAME_REQUIRED_ERROR,
                message: MICROSITE_NAME_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!microSiteBaseInfo.logoFile && !microSiteBaseInfo.logo) {
            addError({
                controlId: MICROSITE_LOGO_CONTROL,
                code: MICROSITE_LOGO_REQUIRED_ERROR,
                message: MICROSITE_LOGO_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (microSiteBaseInfo.logoFile && microSiteBaseInfo.logoFile.size > 10_000_000) {
            addError({
                controlId: MICROSITE_LOGO_CONTROL,
                code: MICROSITE_LOGO_TO_BIG_ERROR,
                message: MICROSITE_LOGO_TO_BIG_MESSAGE,
            });
            valid = false;
        }

        microSiteBaseInfo.domains.forEach((domain, i) => {
            if (!domain) {
                addError({
                    controlId: `${MICROSITE_DOMAINS_CONTROL}-${i}`,
                    code: MICROSITE_DOMAINS_REQUIRED_ERROR,
                    message: MICROSITE_DOMAINS_REQUIRED_MESSAGE,
                });
                valid = false;
            }
        });

        return valid;
    }, [validationErrors, addError]);

    return useCommonDataHook({
        initValue: {
            micrositeName: '',
            logo: '',
            domains: [],
            logoFile: null,
            accountPageInstructions: '',
            loginPageInstructions: '',
            loginPasswordPrompt: '',
            loginUserNamePrompt: '',
        },
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
